import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import dayjs from 'dayjs'
import type { QueryClient } from 'react-query'
import { MAR_CHART_QUERY_KEY } from '../hooks/queryConstants'
import type { AddMedicationFields } from './addMedicationForm'

const toUtcMidnight = (date: string) =>
  `${dayjs(date, 'DD-MM-YYYY').format('YYYY-MM-DD')}T00:00:00.000Z`

/** The request body for a new medication, from the add medication form. */
export const buildMedicationBody = (fields: AddMedicationFields) => {
  const body: AddMedicationFields = {
    ...fields,
    endDateComment: fields.hasEndDate_comment,
    startDate: fields.startDate && toUtcMidnight(fields.startDate),
    endDate: fields.endDate && toUtcMidnight(fields.endDate)
  }

  if (body.PRN) {
    // A PRN medication has no rounds, so its dose moves off the first one.
    // Null values are rejected by the backend, so only set what's there.
    body.emarRoundsDetails = []
    const dose = fields.emarRoundsDetails?.[0]?.dose
    const altDose = fields.emarRoundsDetails?.[0]?.altDose
    if (dose) {
      body.dose = dose
    }
    if (altDose) {
      body.altDose = altDose
    }
  } else {
    body.emarRoundsDetails = (fields.emarRoundsDetails ?? []).filter(
      (round) => round.isChecked
    )
  }

  return body
}

/**
 * Posts `json` to `url`. The V1 modal passes its redux form's `createItem`;
 * the facelift page passes `postMedication`.
 */
type CreateItem = (request: {
  json: AddMedicationFields
  url: string
  onComplete: () => void
  onError: (error: unknown) => void
}) => void

/** `createItem` without redux: posts straight to the API. */
export const postMedication: CreateItem = ({
  json,
  url,
  onComplete,
  onError
}) => {
  postJSON({ url, json }).then(() => onComplete(), onError)
}

type SubmitMedicationOptions = {
  fields: AddMedicationFields
  createItem: CreateItem
  /**
   * Reloads the V1 modal's redux lists. The facelift page needs none: the
   * screens it returns to fetch through react-query when they mount.
   */
  fetchData?: () => void
  emarV2ChartEnabled?: boolean
  queryClient?: QueryClient
  mixpanel?: { track: (event: string) => void }
  /** Set once the second signer has entered their code. */
  secondSignature?: {
    verification: { signerId: string; code: string }
    /** Ties the code to this medication, which has no id until it's saved. */
    residentMedicationId: string
  }
  /** Toast titles, translated by the caller. */
  messages: { saved: string; failed: string }
}

/**
 * Saves a new medication. Shared by the V1 modal and the facelift page, and by
 * the direct save and the verification modal's Confirm alike — there's no
 * separate verify endpoint, the server checks the code here.
 */
export const submitMedication = ({
  fields,
  createItem,
  fetchData,
  emarV2ChartEnabled,
  queryClient,
  mixpanel,
  secondSignature,
  messages
}: SubmitMedicationOptions) =>
  new Promise<void>((resolve, reject) => {
    createItem({
      json: {
        ...buildMedicationBody(fields),
        ...(secondSignature
          ? {
              secondSignatureVerification: secondSignature.verification,
              residentMedicationId: secondSignature.residentMedicationId
            }
          : {})
      },
      url: emarV2ChartEnabled
        ? '/emar/medication-schedule-patterns/legacy/emar-resident-medications'
        : 'list/emar-resident-medications',
      onComplete: () => {
        fetchData?.()
        queryClient?.invalidateQueries([MAR_CHART_QUERY_KEY])
        showToast({ title: messages.saved })
        mixpanel?.track('CO_emar_modal_completed')
        resolve()
      },
      onError: (error) => {
        // The verification modal shows the server's message itself — a
        // generic toast as well would be redundant or contradict it
        if (!secondSignature) {
          console.log(error)
          showToast({ type: 'error', title: messages.failed })
        }
        reject(error)
      }
    })
  })
